import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import { ThemeToggle } from './theme';
import Setup from './components/Setup';
import UserManagement from './components/UserManagement'; 
import SecurityCenter from './components/SecurityCenter';
import CompanyManagement from './components/CompanyManagement';
import config from './config';
import axios from 'axios';
import { askDeleteCode, recentDeleteCode } from './deleteConfirm';
import { installSharedSettingsSync, hydrateSharedSettings } from './sharedSettings';
import DelegateManagement from './components/DelegateManagement';
import ProfessionManagement from './components/ProfessionManagement';
import TestManagement from './components/TestManagement';
import WorkLists from './components/WorkLists';
import PassportManagement from './components/PassportManagement';
import BarcodeSettingsPage from './components/BarcodeSettingsPage';
import Reports from './components/Reports';
import FlightReport from './components/FlightReport';
import DatabaseManagement from './components/DatabaseManagement';
import DatabaseBackup from './components/DatabaseBackup';
import DatabaseDelete from './components/DatabaseDelete';
import SelectionAndPreparation from './components/SelectionAndPreparation';
import VisaManagement from './components/VisaManagement';
import ConsulateManagement from './components/ConsulateManagement';
import PassportReport from './components/PassportReport';
import FingerprintReport from './components/FingerprintReport';
import CompanyTransactions from './components/CompanyTransactions';
import ClientAccounts from './components/ClientAccounts';
import DelegateAccounts from './components/DelegateAccounts';
import FinancialReports from './components/FinancialReports';
import Categories from './components/Categories';
import BanksManagement from './components/BanksManagement';
import HumanResourcesManagement from './components/HumanResourcesManagement';
import DataManagement from './components/DataManagement';
import EmployeeManagement from './components/EmployeeManagement';
import VisaNetRegistration from './components/VisaNetRegistration';
import PeriodicFollowUp from './components/PeriodicFollowUp';
import VisaReports from './components/VisaReports';
import ArchiveManagement from './components/ArchiveManagement';
import SmartRepeat from './components/SmartRepeat';
import APIKeysManager from './components/APIKeysManager';
import InternalChat from './components/InternalChat';
import { Home, FileText, Clock, FileSpreadsheet, DollarSign, Menu, X, Database, Users, CreditCard, Briefcase, Building2, Wallet, ChevronLeft, Save, Upload, User, CheckCircle, AlertCircle } from 'lucide-react';

installSharedSettingsSync(); // الإعدادات المشتركة بين الأجهزة

// ✅ خريطة أسماء الصفحات بالعربي
const PAGE_TITLES = {
  '/': 'لوحة التحكم',
  '/login': 'تسجيل الدخول',
  '/passports': 'إدارة الجوازات',
  '/passports/barcode-settings': 'ضبط الباركود وطرق الطباعة',
  '/reports': 'الكشوفات',
  '/flight-report': 'كشف الطيران',
  '/users': 'إدارة المستخدمين',
  '/security': 'الأمان وسجل الدخول',
  '/companies': 'إدارة الشركات',
  '/delegates': 'إدارة المناديب',
  '/professions': 'إدارة المهن',
  '/tests': 'إدارة الاختبارات',
  '/work-lists': 'قوائم العمل',
  '/selection-prep': 'الاختيار والتجهيز',
  '/visas': 'إدارة التأشيرات',
  '/consulate': 'القنصلية',
  '/passport-report': 'كشف الجوازات',
  '/fingerprint-report': 'تقرير البصمة',
  '/company-transactions': 'إيرادات ومصروفات الشركة',
  '/client-accounts': 'حسابات العملاء',
  '/delegate-accounts': 'حسابات المناديب',
  '/financial-reports': 'التقارير المالية',
  '/categories': 'التصنيفات',
  '/banks': ' الخزينة الشاملة',
  '/employee-accounts': 'حسابات الموظفين',
  '/human-resources': 'الموارد البشرية',
  '/data-management': 'إدارة البيانات',
  '/db-sync': 'مزامنة قاعدة البيانات',
  '/db-backup': 'النسخ الاحتياطي',
  '/db-delete': 'حذف قاعدة البيانات',
  '/archive-management': 'إدارة الأرشيف',
  '/visa-net-registration': 'تسجيل النت',
  '/periodic-follow-up': 'المتابعة الدورية',
  '/visa-reports': 'تقارير التأشيرات',
  '/smart-repeat': 'التكرار الذكي',
  '/api-keys': 'مفاتيح API',
};

// ✅ Component لتحديث عنوان التاب تلقائياً
function PageTitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    // ابحث عن اسم الصفحة بالمسار الكامل أو بداية المسار (للصفحات الديناميكية)
    const exactMatch = PAGE_TITLES[location.pathname];
    const dynamicMatch = Object.entries(PAGE_TITLES).find(([path]) =>
      location.pathname.startsWith(path) && path !== '/'
    );
    const pageTitle = exactMatch || (dynamicMatch ? dynamicMatch[1] : null);
    document.title = pageTitle ? `${pageTitle} |  Passport System` : 'نظام إدارة جوازات السفر';
  }, [location.pathname]);

  return null;
}

// ✅ حماية صارمة من أوامر Agent الوهمية:
// لا ننفذ رابط جاي من الـ Agent إلا لو النسخة الجديدة أرسلته بعد قراءة أمر صريح مثل SEARCH: أو OPEN: أو EDIT:.
const isSafeAgentBarcodeCommand = (params) => {
  const source = (params.get('source') || '').trim().toLowerCase();
  const action = (params.get('barcodeAction') || params.get('action') || params.get('mode') || '').trim().toLowerCase();
  const barcode = String(params.get('barcode') || params.get('code') || params.get('id') || '').trim();
  const hasCommandMarker = ['scannerCommand', 'masarScannerCommand', 'cmd'].some(key => {
    const value = String(params.get(key) || '').trim().toLowerCase();
    return value === '1' || value === 'true' || value === 'yes';
  });

  if (!['search', 'find', 'open', 'view', 'edit'].includes(action)) return false;
  if (!barcode) return false;
  if (/^#?0+$/.test(barcode)) return false;

  // روابط المستخدم المباشرة تظل شغالة، لكن مصدر agent لازم يكون من النسخة الآمنة الجديدة.
  if (source === 'agent' && !hasCommandMarker) return false;
  return true;
};

const stripAgentBarcodeParams = (params) => {
  ['masarRoute', 'barcodeAction', 'barcode', 'code', 'id', 'action', 'mode', 'source', 't', 'scannerCommand', 'masarScannerCommand', 'cmd'].forEach(key => params.delete(key));
  return params;
};



// ✅ يستقبل روابط الـ Agent الخارجية على / فقط ثم ينقل داخليًا إلى صفحة الجوازات
// مثال آمن من خارج React:
// /?masarRoute=passports&barcodeAction=edit&barcode=H-2022&t=123
function MasarExternalRouteHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const masarRoute = (params.get('masarRoute') || '').trim().replace(/^\/+/, '').toLowerCase();

    if (!masarRoute) return;

    if (masarRoute === 'passports' || masarRoute === 'passport') {
      const source = (params.get('source') || '').trim().toLowerCase();
      const barcodeAction = (params.get('barcodeAction') || '').trim();
      if (source === 'agent' && barcodeAction && !isSafeAgentBarcodeCommand(params)) {
        stripAgentBarcodeParams(params);
        const cleanQuery = params.toString();
        navigate({
          pathname: location.pathname || '/',
          search: cleanQuery ? `?${cleanQuery}` : ''
        }, { replace: true });
        return;
      }

      params.delete('masarRoute');
      if (!params.get('t')) params.set('t', String(Date.now()));

      const query = params.toString();
      navigate({
        pathname: '/passports',
        search: query ? `?${query}` : ''
      }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  return null;
}

// ✅ حماية إضافية: لو الرابط وصل على / ومعاه masarRoute، نعمل تحويل فوري قبل ما تظهر لوحة التحكم
function RootRouteGate({ isLoggedIn, children }) {
  const location = useLocation();

  if (isLoggedIn) {
    const params = new URLSearchParams(location.search || '');
    const masarRoute = (params.get('masarRoute') || '').trim().replace(/^\/+/, '').toLowerCase();

    if (masarRoute === 'passports' || masarRoute === 'passport') {
      const source = (params.get('source') || '').trim().toLowerCase();
      const barcodeAction = (params.get('barcodeAction') || '').trim();
      if (source === 'agent' && barcodeAction && !isSafeAgentBarcodeCommand(params)) {
        stripAgentBarcodeParams(params);
        const cleanQuery = params.toString();
        return <Navigate to={`/${cleanQuery ? `?${cleanQuery}` : ''}`} replace />;
      }

      params.delete('masarRoute');
      if (!params.get('t')) params.set('t', String(Date.now()));
      const query = params.toString();
      return <Navigate to={`/passports${query ? `?${query}` : ''}`} replace />;
    }
  }

  return children;
}

function MobileGlobalBottomNav({ isLoggedIn, userPermissions }) {
    const [isMoreOpen, setIsMoreOpen] = useState(false);
    const [openSectionId, setOpenSectionId] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    if (!isLoggedIn || location.pathname === '/login') return null;

    const hasPermission = (section, item) => userPermissions?.[section]?.[item] === true;

    const sections = [
        {
            id: 'data',
            title: 'البيانات',
            icon: <Database className="w-5 h-5" />,
            items: [
                { name: 'الشركات', path: '/companies', icon: <Building2 className="w-5 h-5" /> },
                { name: 'المناديب', path: '/delegates', icon: <User className="w-5 h-5" /> },
                { name: 'المهن', path: '/professions', icon: <Briefcase className="w-5 h-5" /> }
            ]
        },
        {
            id: 'passports',
            title: 'الجوازات',
            icon: <FileText className="w-5 h-5" />,
            items: [
                { name: 'الجوازات', path: '/passports', icon: <FileText className="w-5 h-5" /> },
                { name: 'الاختيار والتجهيزات', path: '/selection-preparation', icon: <CheckCircle className="w-5 h-5" /> },
                { name: 'الكشوفات', displayName: 'كشف الجوازات', path: '/passport-report', icon: <FileSpreadsheet className="w-5 h-5" /> }
            ]
        },
        {
            id: 'visas',
            title: 'التأشيرات والمتابعة',
            icon: <Clock className="w-5 h-5" />,
            items: [
                { name: 'المتابعة الدورية', path: '/periodic-follow-up', icon: <Clock className="w-5 h-5" /> }
            ]
        },
        {
            id: 'accounts',
            title: 'الحسابات',
            icon: <CreditCard className="w-5 h-5" />,
            items: [
                { name: 'الخزينة الشاملة', displayName: 'الخزينة الرئيسية', path: '/banks', icon: <Wallet className="w-5 h-5" /> },
                { name: 'إيرادات ومصروفات الشركة', path: '/company-transactions', icon: <DollarSign className="w-5 h-5" /> },
                { name: 'حسابات العملاء', path: '/client-accounts', icon: <Users className="w-5 h-5" /> },
                { name: 'حسابات المناديب', path: '/delegate-accounts', icon: <User className="w-5 h-5" /> },
                { name: 'حسابات الموظفين', path: '/employee-accounts', icon: <User className="w-5 h-5" /> },
                { name: 'التقارير', path: '/financial-reports', icon: <FileSpreadsheet className="w-5 h-5" /> }
            ]
        },
        {
            id: 'users',
            title: 'المستخدمين',
            icon: <Users className="w-5 h-5" />,
            items: [
                { name: 'المستخدمين', path: '/users', icon: <Users className="w-5 h-5" /> },
                { name: 'الأمان وسجل الدخول', permissionName: 'المستخدمين', path: '/security', icon: <Users className="w-5 h-5" /> }
            ]
        },
        {
            id: 'database',
            title: 'إدارة قاعدة البيانات',
            icon: <Database className="w-5 h-5" />,
            items: [
                { name: 'إدارة البيانات', path: '/data-management', icon: <Upload className="w-5 h-5" /> },
                { name: 'النسخ الاحتياطي وإدارة قاعدة البيانات', path: '/db-backup', icon: <Save className="w-5 h-5" />, warning: true }
            ]
        }
    ];

    const permittedSections = sections
        .map(section => ({
            ...section,
            items: section.items.filter(item => hasPermission(item.permissionSection || (section.title === 'التأشيرات والمتابعة' ? 'التأشيرات' : section.title), item.permissionName || item.name))
        }))
        .filter(section => section.items.length > 0);

    const getPermittedItem = (sectionTitle, itemName, fallbackLabel) => {
        const section = sections.find(s => s.title === sectionTitle);
        if (!section) return null;
        const item = section.items.find(i => i.name === itemName);
        const permissionSection = sectionTitle === 'التأشيرات والمتابعة' ? 'التأشيرات' : sectionTitle;
        return item && hasPermission(permissionSection, item.name) ? { ...item, displayName: fallbackLabel || item.displayName || item.name, sectionTitle: permissionSection } : null;
    };

    const mainNavItems = [
        { name: 'الرئيسية', displayName: 'الرئيسية', path: '/', icon: <Home className="w-5 h-5" />, always: true },
        getPermittedItem('الجوازات', 'الجوازات'),
        getPermittedItem('التأشيرات والمتابعة', 'المتابعة الدورية', 'المتابعة'),
        getPermittedItem('الجوازات', 'الكشوفات', 'كشف الجوازات'),
        getPermittedItem('الحسابات', 'إيرادات ومصروفات الشركة', 'الإيرادات')
    ].filter(Boolean);

    // عدد الأعمدة يتغير حسب الصلاحيات حتى لا يعمل الشريط عرض زائد على الموبايل
    const bottomNavColumnCount = Math.max(
        mainNavItems.length + (permittedSections.length > 0 ? 1 : 0),
        1
    );

    const goTo = (path, warning = false) => {
        if (warning && !window.confirm('هذا الإجراء خطير. هل أنت متأكد من المتابعة؟')) return;
        setIsMoreOpen(false);
        navigate(path);
    };

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <>
            <style>{`
                @media (max-width: 767px) {
                    html,
                    body,
                    #root {
                        width: 100%;
                        max-width: 100vw;
                        overflow-x: hidden !important;
                    }

                    * {
                        box-sizing: border-box;
                    }

                    body {
                        padding-bottom: calc(86px + env(safe-area-inset-bottom));
                    }

                    .masar-mobile-global-nav {
                        display: block;
                        width: 100vw !important;
                        max-width: 100vw !important;
                        overflow-x: hidden !important;
                    }

                    .masar-mobile-global-nav * {
                        min-width: 0;
                        box-sizing: border-box;
                    }
                }

                @media (min-width: 768px) {
                    .masar-mobile-global-nav {
                        display: none !important;
                    }
                }
            `}</style>

            {isMoreOpen && (
                <div className="masar-mobile-global-nav fixed inset-0 z-[9998] w-screen max-w-[100vw] overflow-hidden bg-black/45" dir="rtl">
                    <button
                        className="absolute inset-0 w-full h-full"
                        onClick={() => setIsMoreOpen(false)}
                        aria-label="إغلاق القائمة"
                    />
                    <div className="absolute bottom-0 inset-x-0 w-screen max-w-[100vw] bg-white rounded-t-[28px] shadow-2xl max-h-[78vh] overflow-hidden">
                        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-black text-slate-900">كل أقسام لوحة التحكم</h3>
                                <p className="text-xs text-slate-500 mt-1">اختر القسم ثم الصفحة المطلوبة</p>
                            </div>
                            <button
                                onClick={() => setIsMoreOpen(false)}
                                className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-700 flex items-center justify-center"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-3 space-y-2 overflow-y-auto max-h-[calc(78vh-78px)]">
                            {permittedSections.map(section => {
                                const isOpen = openSectionId === section.id;
                                return (
                                    <div key={section.id} className="rounded-2xl border border-slate-100 bg-slate-50 overflow-hidden">
                                        <button
                                            onClick={() => setOpenSectionId(isOpen ? null : section.id)}
                                            className="w-full p-3 flex items-center justify-between text-right"
                                        >
                                            <span className="flex items-center gap-3 min-w-0">
                                                <span className="w-10 h-10 rounded-xl bg-white text-blue-600 flex items-center justify-center shadow-sm">
                                                    {section.icon}
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block font-extrabold text-sm text-slate-900">{section.title}</span>
                                                    <span className="block text-[11px] text-slate-500 mt-0.5 truncate">{section.items.length} صفحة متاحة</span>
                                                </span>
                                            </span>
                                            <ChevronLeft className={`w-5 h-5 text-slate-400 transition-transform ${isOpen ? '-rotate-90' : ''}`} />
                                        </button>

                                        {isOpen && (
                                            <div className="px-3 pb-3 grid grid-cols-1 gap-2">
                                                {section.items.map(item => (
                                                    <button
                                                        key={`${section.id}-${item.name}`}
                                                        onClick={() => goTo(item.path, item.warning)}
                                                        className="w-full rounded-xl bg-white border border-slate-100 p-3 flex items-center justify-between text-right"
                                                    >
                                                        <span className="flex items-center gap-3 min-w-0">
                                                            <span className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center">
                                                                {item.icon}
                                                            </span>
                                                            <span className="font-bold text-sm text-slate-800 truncate">{item.displayName || item.name}</span>
                                                        </span>
                                                        {item.warning ? <AlertCircle className="w-5 h-5 text-red-500" /> : <ChevronLeft className="w-5 h-5 text-slate-400" />}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            <div
                className="masar-mobile-global-nav fixed bottom-0 inset-x-0 z-[9997] w-screen max-w-[100vw] overflow-hidden bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-[0_-8px_24px_rgba(15,23,42,0.08)]"
                dir="rtl"
            >
                <div
                    className="grid w-full max-w-full overflow-hidden"
                    style={{ gridTemplateColumns: `repeat(${bottomNavColumnCount}, minmax(0, 1fr))` }}
                >
                    {mainNavItems.map(item => (
                        <button
                            key={item.path}
                            onClick={() => goTo(item.path, item.warning)}
                            className={`min-w-0 overflow-hidden py-2.5 flex flex-col items-center justify-center gap-1 ${isActive(item.path) ? 'text-blue-600' : 'text-slate-500'}`}
                        >
                            <span className={`w-9 h-9 shrink-0 rounded-2xl flex items-center justify-center ${isActive(item.path) ? 'bg-blue-50' : 'bg-transparent'}`}>
                                {item.icon}
                            </span>
                            <span className="block w-full min-w-0 max-w-full truncate px-0.5 text-center text-[10px] font-extrabold leading-4">
                                {item.displayName || item.name}
                            </span>
                        </button>
                    ))}

                    {permittedSections.length > 0 && (
                        <button
                            onClick={() => setIsMoreOpen(true)}
                            className="min-w-0 overflow-hidden py-2.5 flex flex-col items-center justify-center gap-1 text-slate-500"
                        >
                            <span className="w-9 h-9 shrink-0 rounded-2xl bg-slate-100 flex items-center justify-center">
                                <Menu className="w-5 h-5" />
                            </span>
                            <span className="block w-full min-w-0 max-w-full truncate px-0.5 text-center text-[10px] font-extrabold leading-4">المزيد</span>
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}


function FloatingQuickNavigator({ isLoggedIn, userPermissions }) {
    const [isOpen, setIsOpen] = useState(false);
    const [openSectionId, setOpenSectionId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const navigate = useNavigate();

    const hasPermission = (section, item) => userPermissions?.[section]?.[item] === true;

    const canOpenItem = (section, item) => {
        if (item.always) return true;
        if (Array.isArray(item.permissionAny)) {
            return item.permissionAny.some(([permSection, permItem]) => hasPermission(permSection, permItem));
        }
        return hasPermission(item.permissionSection || section.title, item.permissionName || item.name);
    };

    const sections = [
        {
            id: 'home',
            title: 'الرئيسية',
            icon: <Home className="w-5 h-5" />,
            directPath: '/',
            always: true,
            items: []
        },
        {
            id: 'data',
            title: 'البيانات',
            icon: <Database className="w-5 h-5" />,
            items: [
                { name: 'الشركات', path: '/companies', icon: <Building2 className="w-4 h-4" /> },
                { name: 'المناديب', path: '/delegates', icon: <User className="w-4 h-4" /> },
                { name: 'المهن', path: '/professions', icon: <Briefcase className="w-4 h-4" /> }
            ]
        },
        {
            id: 'passports',
            title: 'الجوازات',
            icon: <FileText className="w-5 h-5" />,
            items: [
                { name: 'الجوازات', path: '/passports', icon: <FileText className="w-4 h-4" /> },
                { name: 'الاختيار والتجهيزات', path: '/selection-preparation', icon: <CheckCircle className="w-4 h-4" /> },
                { name: 'الكشوفات', path: '/reports', icon: <FileSpreadsheet className="w-4 h-4" /> }
            ]
        },
        {
            id: 'visas',
            title: 'التأشيرات والمتابعة',
            icon: <Clock className="w-5 h-5" />,
            items: [
                { name: 'تسجيل النت', path: '/visa-net-registration', icon: <Clock className="w-4 h-4" />, permissionSection: 'التأشيرات' },
                { name: 'السفر', path: '/visas', icon: <Clock className="w-4 h-4" />, permissionSection: 'التأشيرات' },
                { name: 'القنصلية', path: '/consulate', icon: <Clock className="w-4 h-4" />, permissionSection: 'التأشيرات' },
                { name: 'المتابعة الدورية', path: '/periodic-follow-up', icon: <Clock className="w-4 h-4" />, permissionSection: 'التأشيرات' },
                { name: 'تقارير التأشيرات', path: '/visa-reports', icon: <FileSpreadsheet className="w-4 h-4" />, permissionSection: 'التأشيرات' }
            ]
        },
        {
            id: 'accounts',
            title: 'الحسابات',
            icon: <CreditCard className="w-5 h-5" />,
            items: [
                { name: 'الخزينة الشاملة', path: '/banks', icon: <Wallet className="w-4 h-4" /> },
                { name: 'إيرادات ومصروفات الشركة', path: '/company-transactions', icon: <DollarSign className="w-4 h-4" /> },
                { name: 'حسابات العملاء', path: '/client-accounts', icon: <Users className="w-4 h-4" /> },
                { name: 'حسابات المناديب', path: '/delegate-accounts', icon: <User className="w-4 h-4" /> },
                { name: 'حسابات الموظفين', path: '/employee-accounts', icon: <User className="w-4 h-4" /> },
                { name: 'التقارير', path: '/financial-reports', icon: <FileSpreadsheet className="w-4 h-4" /> },
                { name: 'التصنيفات', path: '/categories', icon: <Menu className="w-4 h-4" /> }
            ]
        },
        {
            id: 'users',
            title: 'المستخدمين',
            icon: <Users className="w-5 h-5" />,
            items: [
                { name: 'المستخدمين', path: '/users', icon: <Users className="w-4 h-4" /> },
                { name: 'الأمان وسجل الدخول', permissionName: 'المستخدمين', path: '/security', icon: <Users className="w-4 h-4" /> }
            ]
        },
        {
            id: 'database',
            title: 'إدارة قاعدة البيانات',
            icon: <Database className="w-5 h-5" />,
            items: [
                { name: 'إدارة البيانات', path: '/data-management', icon: <Upload className="w-4 h-4" /> },
                { name: 'مزامنة البيانات', path: '/db-sync', icon: <Database className="w-4 h-4" /> },
                {
                    name: 'النسخ الاحتياطي وإدارة قاعدة البيانات',
                    path: '/db-backup',
                    icon: <Save className="w-4 h-4" />,
                    warning: true,
                    permissionAny: [
                        ['إدارة قاعدة البيانات', 'النسخ الاحتياطي'],
                        ['إدارة قاعدة البيانات', 'النسخ الاحتياطي وإدارة قاعدة البيانات']
                    ]
                },
                { name: 'إدارة الأرشيف', path: '/archive-management', icon: <Database className="w-4 h-4" /> },
                { name: 'حذف قاعدة البيانات', path: '/db-delete', icon: <AlertCircle className="w-4 h-4" />, warning: true }
            ]
        },
        {
            id: 'hr',
            title: 'الموارد البشرية',
            icon: <Users className="w-5 h-5" />,
            items: [
                { name: 'إدارة الموارد البشرية', path: '/human-resources', icon: <Users className="w-4 h-4" />, permissionSection: 'إدارة الموارد البشرية' }
            ]
        }
    ];

    const permittedSections = sections
        .map(section => section.directPath ? section : ({
            ...section,
            items: section.items.filter(item => canOpenItem(section, item))
        }))
        .filter(section => section.directPath || section.items.length > 0);

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const visibleSections = permittedSections
        .map(section => {
            if (section.directPath) return section;
            return {
                ...section,
                items: normalizedSearch
                    ? section.items.filter(item => `${section.title} ${item.name}`.toLowerCase().includes(normalizedSearch))
                    : section.items
            };
        })
        .filter(section => {
            if (section.directPath) {
                return !normalizedSearch || section.title.toLowerCase().includes(normalizedSearch);
            }
            return section.items.length > 0;
        });

    useEffect(() => {
        setIsOpen(false);
        setOpenSectionId(null);
        setSearchTerm('');
    }, [location.pathname]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                setOpenSectionId(null);
                setSearchTerm('');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    if (!isLoggedIn || location.pathname === '/login') return null;

    const goTo = (path, warning = false) => {
        if (warning && !window.confirm('هذا الإجراء حساس. هل أنت متأكد من المتابعة؟')) return;
        setIsOpen(false);
        setOpenSectionId(null);
        setSearchTerm('');
        navigate(path);
    };

    const isActive = (path) => {
        if (path === '/') return location.pathname === '/';
        return location.pathname === path || location.pathname.startsWith(`${path}/`);
    };

    return (
        <>
            {isOpen && (
                <button
                    type="button"
                    className="fixed inset-0 z-[10030] bg-transparent cursor-default"
                    aria-label="إغلاق قائمة التنقل السريع"
                    onClick={() => {
                        setIsOpen(false);
                        setOpenSectionId(null);
                        setSearchTerm('');
                    }}
                />
            )}

            <div className="hidden md:block fixed bottom-5 right-5 z-[10040]" dir="rtl">
                <button
                    type="button"
                    onClick={() => {
                        setIsOpen(prev => {
                            const next = !prev;
                            setOpenSectionId(null);
                            setSearchTerm('');
                            return next;
                        });
                    }}
                    className={`group w-12 h-12 rounded-2xl shadow-xl border border-gray-200 flex items-center justify-center transition-all duration-200 ${
                        isOpen
                            ? 'bg-[#0f1d3d] text-[#e0c070] scale-95'
                            : 'bg-white/95 backdrop-blur-md text-navy-800 hover:text-gold-700 hover:bg-gold-50 hover:shadow-2xl'
                    }`}
                    title="التنقل السريع"
                    aria-label="التنقل السريع بين صفحات النظام"
                >
                    {isOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </button>

                {isOpen && (
                    <div
                        className="absolute bottom-14 right-0 rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
                        style={{ width: 'min(390px, calc(100vw - 2rem))' }}
                    >
                        <div className="p-4 bg-gradient-to-l from-[#1b3163] via-[#0f1d3d] to-[#0a142b] text-white">
                            <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                    <h3 className="font-black text-base">التنقل السريع</h3>
                                    <p className="text-xs text-blue-100 mt-1">الصفحات المتاحة حسب صلاحياتك فقط</p>
                                </div>
                                <ThemeToggle className="!rounded-2xl !border-[#fff]/20 !bg-[#fff]/10 !text-white hover:!text-[#e0c070] shrink-0" />
                            </div>

                            <div className="mt-3 relative">
                                <input
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="ابحث عن صفحة..."
                                    className="w-full rounded-2xl border border-[#fff]/20 bg-[#fff]/10 px-4 py-2.5 pr-10 text-sm text-white placeholder:text-[#b4c3e0] outline-none focus:bg-[#fff]/15 focus:border-[#d4ab4c]/60"
                                />
                                <Menu className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-100" />
                            </div>
                        </div>

                        <div className="max-h-[calc(100vh-14rem)] overflow-y-auto p-3 bg-slate-50 space-y-2">
                            {visibleSections.length === 0 && (
                                <div className="p-5 text-center text-sm text-slate-500 bg-white rounded-2xl border border-dashed border-slate-200">
                                    لا توجد صفحة مطابقة للبحث
                                </div>
                            )}

                            {visibleSections.map(section => {
                                const sectionOpen = !section.directPath && (openSectionId === section.id || Boolean(normalizedSearch));
                                return (
                                    <div key={section.id} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                                        <button
                                            type="button"
                                            onClick={() => section.directPath ? goTo(section.directPath) : setOpenSectionId(sectionOpen ? null : section.id)}
                                            className={`w-full p-3 flex items-center justify-between text-right transition-colors ${section.directPath ? 'hover:bg-blue-50' : 'hover:bg-slate-50'}`}
                                        >
                                            <span className="flex items-center gap-3 min-w-0">
                                                <span className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 ${section.directPath ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>
                                                    {section.icon}
                                                </span>
                                                <span className="min-w-0">
                                                    <span className="block font-black text-sm text-slate-900 truncate">{section.title}</span>
                                                    <span className="block text-[11px] text-slate-500 mt-0.5">
                                                        {section.directPath ? 'انتقال مباشر' : `${section.items.length} صفحة متاحة`}
                                                    </span>
                                                </span>
                                            </span>
                                            <ChevronLeft className={`w-5 h-5 text-slate-400 transition-transform ${section.directPath ? '' : (sectionOpen ? '-rotate-90' : '')}`} />
                                        </button>

                                        {!section.directPath && sectionOpen && (
                                            <div className="px-3 pb-3 grid grid-cols-1 gap-2">
                                                {section.items.map(item => (
                                                    <button
                                                        type="button"
                                                        key={`${section.id}-${item.path}`}
                                                        onClick={() => goTo(item.path, item.warning)}
                                                        className={`w-full rounded-2xl border p-3 flex items-center justify-between text-right transition-all ${
                                                            isActive(item.path)
                                                                ? 'bg-blue-50 border-blue-200 text-blue-800'
                                                                : 'bg-white border-slate-100 text-slate-700 hover:border-blue-200 hover:bg-blue-50/60'
                                                        }`}
                                                    >
                                                        <span className="flex items-center gap-3 min-w-0">
                                                            <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isActive(item.path) ? 'bg-blue-100' : 'bg-slate-100'}`}>
                                                                {item.icon}
                                                            </span>
                                                            <span className="font-extrabold text-sm truncate">{item.name}</span>
                                                        </span>
                                                        {item.warning ? (
                                                            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                                                        ) : (
                                                            <ChevronLeft className="w-4 h-4 text-slate-300 shrink-0" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}                        </div>
                    </div>
                )}
            </div>
        </>
    );
}


function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  // 🔐 حماية حذف عامة على مستوى الواجهة: أي DELETE بيطلب «رمز تأكيد الحذف» (بدل الرقم الثابت 123).
  // الرمز بيتحدد في إعدادات السيرفر، والسيرفر هو اللي بيتأكد منه.
  // الصفحات اللي سألت عن الرمز بنفسها لسه ما تسألش تاني.
  useEffect(() => {
      if (typeof window === 'undefined' || typeof window.fetch !== 'function') return undefined;
      const originalFetch = window.fetch.bind(window);
      const needCode = (value) => {
          const v = String(value || '').trim();
          return !v || v === '123';
      };
      const cancelled = () => {
          const error = new Error('تم إلغاء الحذف');
          error.name = 'DeleteConfirmationError';
          return error;
      };
      const secureFetch = async (input, init = {}) => {
          const requestMethod = String(
              init?.method || (typeof Request !== 'undefined' && input instanceof Request ? input.method : 'GET')
          ).toUpperCase();
          let response;
          if (requestMethod !== 'DELETE') {
              response = await originalFetch(input, init);
          } else {
              const headers = new Headers(
                  typeof Request !== 'undefined' && input instanceof Request ? input.headers : undefined
              );
              if (init?.headers) {
                  new Headers(init.headers).forEach((value, key) => headers.set(key, value));
              }
              if (needCode(headers.get('X-Delete-Confirm'))) {
                  const code = recentDeleteCode() || askDeleteCode();
                  if (!code) throw cancelled();
                  headers.set('X-Delete-Confirm', code);
              }
              response = await originalFetch(input, { ...init, headers });
          }
          // الجلسة خلصت (أو اتسجل خروج من جهاز تاني): نرجع لصفحة الدخول
          if (response.status === 401) {
              const url = String(typeof input === 'string' ? input : input?.url || '');
              if (url.includes('/api/') && !url.includes('/api/login') && localStorage.getItem('user')) {
                  localStorage.removeItem('user');
                  localStorage.removeItem('userPermissions');
                  window.location.assign('/login');
              }
          }
          return response;
      };
      window.fetch = secureFetch;
      const axiosInterceptor = axios.interceptors.request.use((cfg) => {
          if (String(cfg.method || '').toUpperCase() !== 'DELETE') return cfg;
          const h = cfg.headers;
          const current = (h && typeof h.get === 'function') ? h.get('X-Delete-Confirm') : (h?.['X-Delete-Confirm'] || h?.['x-delete-confirm']);
          if (needCode(current)) {
              const code = recentDeleteCode() || askDeleteCode();
              if (!code) return Promise.reject(cancelled());
              if (h && typeof h.set === 'function') h.set('X-Delete-Confirm', code);
              else cfg.headers = { ...(h || {}), 'X-Delete-Confirm': code };
          }
          return cfg;
      });
      return () => {
          if (window.fetch === secureFetch) window.fetch = originalFetch;
          axios.interceptors.request.eject(axiosInterceptor);
      };
  }, []);

  // ✅ Screen Lock
  // localhost / 127.0.0.1 = قفل تلقائي بعد 40 دقيقة بدون تفاعل
  // أجهزة الشبكة = لا تقفل أثناء الشغل، لكن لو المتصفح اتقفل ورجع بعد 3 أيام يظهر القفل
  const LOCAL_LOCK_TIMEOUT = 40 * 60 * 1000; // 40 دقيقة للجهاز الرئيسي
  const NETWORK_BROWSER_REOPEN_TIMEOUT = 3 * 24 * 60 * 60 * 1000; // 3 أيام لأجهزة الشبكة بعد غلق المتصفح
  const SCREEN_LOCK_KEYS = {
      state: 'masar_screen_lock_state',
      unlockedUntil: 'masar_screen_unlocked_until',
      lastActivity: 'masar_last_activity',
      lastBrowserSeen: 'masar_last_browser_seen',
      mode: 'masar_screen_lock_mode'
  };

  const isMainLocalDevice = () => {
      const host = String(window.location.hostname || '').toLowerCase();
      return host === 'localhost' || host === '127.0.0.1' || host === '::1';
  };

  const getScreenLockTimeout = () => (
      isMainLocalDevice() ? LOCAL_LOCK_TIMEOUT : NETWORK_BROWSER_REOPEN_TIMEOUT
  );

  const readNumberFromStorage = (key, fallback = 0) => {
      const value = Number(localStorage.getItem(key));
      return Number.isFinite(value) ? value : fallback;
  };

  const isSharedUnlockActive = () => {
      const lockState = localStorage.getItem(SCREEN_LOCK_KEYS.state);
      if (lockState !== 'unlocked') return false;

      const now = Date.now();

      if (isMainLocalDevice()) {
          const unlockedUntil = readNumberFromStorage(SCREEN_LOCK_KEYS.unlockedUntil);
          return unlockedUntil > now;
      }

      // على أجهزة الشبكة: اعتبر الجهاز مفتوح طالما آخر ظهور للمتصفح لم يتجاوز 3 أيام
      // يتم تحديث lastBrowserSeen أثناء فتح التاب، وعند غلق/تحديث المتصفح.
      const lastBrowserSeen = readNumberFromStorage(SCREEN_LOCK_KEYS.lastBrowserSeen);
      if (!lastBrowserSeen) {
          const unlockedUntil = readNumberFromStorage(SCREEN_LOCK_KEYS.unlockedUntil);
          return unlockedUntil > now;
      }

      return (now - lastBrowserSeen) <= NETWORK_BROWSER_REOPEN_TIMEOUT;
  };

  // ✅ حالة القفل بقت localStorage بدل sessionStorage عشان أي تاب جديدة تعرف إن السيستم مفتوح
  const [isLocked, setIsLocked] = useState(() => {
      const hasUser = !!localStorage.getItem('user');
      if (!hasUser) return false;
      return !isSharedUnlockActive();
  });
  const [lockPassword, setLockPassword] = useState('');
  const [lockError, setLockError] = useState('');
  const [lockLoading, setLockLoading] = useState(false);

  const markScreenUnlocked = () => {
      const now = Date.now();
      localStorage.setItem(SCREEN_LOCK_KEYS.state, 'unlocked');
      localStorage.setItem(SCREEN_LOCK_KEYS.unlockedUntil, String(now + getScreenLockTimeout()));
      localStorage.setItem(SCREEN_LOCK_KEYS.lastActivity, String(now));
      localStorage.setItem(SCREEN_LOCK_KEYS.lastBrowserSeen, String(now));
      localStorage.setItem(SCREEN_LOCK_KEYS.mode, isMainLocalDevice() ? 'local' : 'network');

      // تنظيف المفاتيح القديمة حتى لا تفتح تاب وتجد sessionStorage قديم متعارض
      sessionStorage.removeItem('screenLocked');
      sessionStorage.removeItem('screenUnlocked');
  };

  const markScreenLocked = () => {
      localStorage.setItem(SCREEN_LOCK_KEYS.state, 'locked');
      localStorage.removeItem(SCREEN_LOCK_KEYS.unlockedUntil);
      localStorage.setItem(SCREEN_LOCK_KEYS.lastActivity, String(Date.now()));
      localStorage.setItem(SCREEN_LOCK_KEYS.lastBrowserSeen, String(Date.now()));
      localStorage.setItem(SCREEN_LOCK_KEYS.mode, isMainLocalDevice() ? 'local' : 'network');

      // مفاتيح قديمة للتوافق مع أي كود قديم
      sessionStorage.setItem('screenLocked', 'true');
      sessionStorage.removeItem('screenUnlocked');
  };

  const refreshSharedActivity = () => {
      if (isLocked || !isLoggedIn) return;
      markScreenUnlocked();
  };

  useEffect(() => {
      checkSystemConfiguration();
  }, []);

  // ✅ Screen Lock مشترك بين التابات مع اختلاف سلوك localhost عن أجهزة الشبكة
  useEffect(() => {
      if (!isLoggedIn) return;

      const isLocalDevice = isMainLocalDevice();
      let timer;
      let heartbeatTimer;

      const lockNow = () => {
          clearTimeout(timer);
          clearInterval(heartbeatTimer);
          markScreenLocked();
          setIsLocked(true);
          setLockPassword('');
          setLockError('');
      };

      const scheduleLocalLock = () => {
          clearTimeout(timer);

          // أجهزة الشبكة لا تقفل أثناء فتح المتصفح، القفل هناك عند الرجوع بعد أكثر من 3 أيام
          if (!isLocalDevice) return;

          if (!isSharedUnlockActive()) {
              lockNow();
              return;
          }

          const unlockedUntil = readNumberFromStorage(SCREEN_LOCK_KEYS.unlockedUntil);
          const remaining = Math.max(unlockedUntil - Date.now(), 1000);
          timer = setTimeout(lockNow, remaining);
      };

      const startNetworkHeartbeat = () => {
          if (isLocalDevice) return;
          clearInterval(heartbeatTimer);
          heartbeatTimer = setInterval(() => {
              if (!isLocked && isSharedUnlockActive()) {
                  markScreenUnlocked();
              }
          }, 60 * 1000);
      };

      // عند فتح تاب جديدة:
      // localhost: يفتح فقط لو مدة عدم النشاط لم تنتهِ.
      // الشبكة: يفتح لو المتصفح لم يكن مغلقًا أكثر من 3 أيام.
      if (isSharedUnlockActive()) {
          setIsLocked(false);
          markScreenUnlocked();
          startNetworkHeartbeat();
      } else {
          lockNow();
      }

      const handleUserActivity = () => {
          if (isLocked) return;
          markScreenUnlocked();
          scheduleLocalLock();
          startNetworkHeartbeat();
      };

      const handleBeforeUnload = () => {
          if (!isLocked) {
              localStorage.setItem(SCREEN_LOCK_KEYS.lastBrowserSeen, String(Date.now()));
          }
      };

      const handleVisibilityChange = () => {
          if (!document.hidden && !isLocked && isSharedUnlockActive()) {
              markScreenUnlocked();
              scheduleLocalLock();
              startNetworkHeartbeat();
          } else if (document.hidden && !isLocked) {
              localStorage.setItem(SCREEN_LOCK_KEYS.lastBrowserSeen, String(Date.now()));
          }
      };

      const handleStorageSync = (event) => {
          if (!event.key || !Object.values(SCREEN_LOCK_KEYS).includes(event.key)) return;

          if (isSharedUnlockActive()) {
              setIsLocked(false);
              setLockError('');
              scheduleLocalLock();
              startNetworkHeartbeat();
          } else {
              setIsLocked(true);
          }
      };

      const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
      events.forEach((eventName) => window.addEventListener(eventName, handleUserActivity, { passive: true }));
      window.addEventListener('storage', handleStorageSync);
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      scheduleLocalLock();
      startNetworkHeartbeat();

      return () => {
          if (!isLocked) {
              localStorage.setItem(SCREEN_LOCK_KEYS.lastBrowserSeen, String(Date.now()));
          }
          clearTimeout(timer);
          clearInterval(heartbeatTimer);
          events.forEach((eventName) => window.removeEventListener(eventName, handleUserActivity));
          window.removeEventListener('storage', handleStorageSync);
          window.removeEventListener('beforeunload', handleBeforeUnload);
          document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
  }, [isLoggedIn, isLocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnlock = async () => {
      if (!lockPassword.trim()) return;
      setLockLoading(true);
      setLockError('');

      try {
          const baseURL = config.API_BASE_URL; // نفس الموقع
          // التأكد من كلمة المرور بس (الجلسة نفسها شغالة) — من غير دخول جديد ومن غير كود التأكيد بخطوتين
          const response = await fetch(`${baseURL}/api/session/verify-password`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ password: lockPassword })
          });

          const data = await response.json();

          if (data.success) {
              markScreenUnlocked();
              setIsLocked(false);
              setLockPassword('');
              setLockError('');

              // ✅ لو القفل اتفك من لينك باركود، سيب الصفحة الحالية تكمل تحميلها وتنفذ الأمر
              const url = new URL(window.location.href);
              if (url.searchParams.get('barcodeAction') && url.searchParams.get('barcode')) {
                  url.searchParams.set('t', String(Date.now()));
                  window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
              }
          } else {
              setLockError('كلمة المرور غير صحيحة');
          }
      } catch (err) {
          setLockError('خطأ في الاتصال بالخادم');
      } finally {
          setLockLoading(false);
      }
  };

  // ✅ تحديث الصلاحيات تلقائياً كل 30 ثانية - نفس أسلوب سيستم المستشفى
  useEffect(() => {
      if (!isLoggedIn) return;

      const refreshPermissions = async () => {
          try {
              const userData = localStorage.getItem('user');
              if (!userData) return;

              const user = JSON.parse(userData);
              if (user.UserId === -1) return; // superadmin - مش محتاج فحص

              // ✅ دايماً استخدم hostname المتصفح كـ IP أساسي
              const baseURL = config.API_BASE_URL; // نفس الموقع

              const response = await fetch(`${baseURL}/api/verify-token`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ userId: user.UserId, username: user.Username })
              });

              // ✅ 401 = المستخدم اتحذف - logout فوري
              if (response.status === 401) {
                  console.warn('⚠️ 401 في الـ polling - المستخدم اتحذف - تسجيل الخروج');
                  handleLogout();
                  return;
              }

              if (!response.ok) return; // أخطاء شبكة أخرى - نتجاهل

              const data = await response.json();

              if (data.success && data.user) {
                  // ✅ مستخدم موجود - نحدث صلاحياته
                  const freshUser = data.user;
                  const freshPermissions = JSON.parse(freshUser.Permissions || '{}');

                  localStorage.setItem('user', JSON.stringify(freshUser));
                  localStorage.setItem('userPermissions', JSON.stringify(freshPermissions));

                  setCurrentUser(freshUser);
                  setUserPermissions(freshPermissions);
              } else if (!data.success) {
                  // المستخدم اتحذف أثناء جلسته - logout تلقائي
                  console.warn('⚠️ المستخدم اتحذف أثناء الجلسة - تسجيل الخروج');
                  handleLogout();
              }
          } catch (err) {
              // فشل الشبكة - نتجاهل بهدوء
          }
      };

      const interval = setInterval(refreshPermissions, 30000);
      return () => clearInterval(interval);
  }, [isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkSystemConfiguration = async () => {
    try {
        // النسخة الأونلاين: السيرفر على نفس عنوان الموقع، فمفيش IP يتحفظ
        const correctIP = window.location.hostname;
        const correctBaseURL = config.API_BASE_URL;
        localStorage.removeItem('manualServerIP');
        localStorage.removeItem('systemURLs_backup');

        // ✅ دايماً اكتب الـ config الصحيح بالـ IP الحالي
        const correctConfig = {
            API_BASE_URL: correctBaseURL,
            PASSPORT_API_URL: correctBaseURL,
            TREASURY_API_URL: correctBaseURL,
            REPORTS_API_URL: correctBaseURL,
            WHATSAPP_API_URL: correctBaseURL,
            WHATSAPP_WS_URL: config.WS_BASE_URL,
            FLIGHT_API_URL: correctBaseURL,
            COMPANY_TRANSACTIONS_API_URL: correctBaseURL,
            BANKS_API_URL: correctBaseURL,
            CLIENTS_API_URL: correctBaseURL,
            DELEGATES_API_URL: correctBaseURL,
            COMPANY_MANAGEMENT_API_URL: correctBaseURL,
            DELEGATES_MANAGEMENT_API_URL: correctBaseURL,
            PROFESSIONS_API_URL: correctBaseURL,
            VISA_MANAGEMENT_API_URL: correctBaseURL,
            VISA_REPORTS_API_URL: correctBaseURL,
            USER_MANAGEMENT_API_URL: correctBaseURL,
            HUMAN_RESOURCES_API_URL: correctBaseURL,
            FINANCIAL_REPORTS_API_URL: correctBaseURL,
            PERIODIC_FOLLOW_UP_API_URL: correctBaseURL,
            CONSULATE_PREPARATION_API_URL: correctBaseURL,
            ARCHIVE_MANAGEMENT_API_URL: correctBaseURL,
            DATA_IMPORT_API_URL: correctBaseURL,
            lastUpdate: new Date().toISOString(),
            serverIP: correctIP
        };

        localStorage.setItem('systemURLs', JSON.stringify(correctConfig));
        localStorage.setItem('systemConfig', JSON.stringify({ baseIP: correctIP, lastUpdate: new Date().toISOString() }));

        console.log('✅ API_BASE_URL الصحيح:', correctBaseURL);

        // تابع فحص المصادقة
        await checkUserAuthentication();
    } catch (error) {
        console.error('خطأ في فحص تكوين النظام:', error);
        setIsLoading(false);
    }
  };

const checkUserAuthentication = async () => {
    try {
        const userData = localStorage.getItem('user');
        if (!userData) {
            // دخول من موقع الشركة (أو جلسة لسه شغالة): السيرفر عارف المستخدم من الكوكي
            try {
                const r = await fetch(`${config.API_BASE_URL}/api/session`);
                const data = r.ok ? await r.json() : null;
                if (data && data.success && data.user) {
                    const sessionUser = data.user;
                    const sessionPermissions = JSON.parse(sessionUser.Permissions || '{}');
                    localStorage.setItem('user', JSON.stringify(sessionUser));
                    localStorage.setItem('userPermissions', JSON.stringify(sessionPermissions));
                    localStorage.setItem('userId', sessionUser.UserId);
                    localStorage.setItem('fullName', sessionUser.FullName);
                    await hydrateSharedSettings();
                    markScreenUnlocked();
                    setIsLoggedIn(true);
                    setCurrentUser(sessionUser);
                    setUserPermissions(sessionPermissions);
                }
            } catch (e) { /* مفيش جلسة: صفحة الدخول */ }
            if (window.location.search.includes('sso=1')) window.history.replaceState(null, '', window.location.pathname);
            setIsLoading(false);
            return;
        }

        const user = JSON.parse(userData);
        
        // التحقق من صحة البيانات مع الخادم
        // ✅ دايماً استخدم hostname المتصفح كـ IP أساسي
        const loginURL = config.API_BASE_URL; // نفس الموقع
        
        console.log('محاولة التحقق من المصادقة على:', `${loginURL}/api/verify-token`);
        
        const response = await fetch(`${loginURL}/api/verify-token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                userId: user.UserId,
                username: user.Username
            })
        });

        if (response.ok) {
            const data = await response.json();
            if (data.success && data.user) {
                // ✅ مستخدم موجود - نحدث بياناته من السيرفر
                const freshUser = data.user;
                const freshPermissions = JSON.parse(freshUser.Permissions || '{}');

                localStorage.setItem('user', JSON.stringify(freshUser));
                localStorage.setItem('userPermissions', JSON.stringify(freshPermissions));

                await hydrateSharedSettings();
                setIsLoggedIn(true);
                setCurrentUser(freshUser);
                setUserPermissions(freshPermissions);
            } else {
                // ✅ المستخدم اتحذف من قاعدة البيانات - logout تلقائي
                console.warn('⚠️ المستخدم غير موجود في قاعدة البيانات - تسجيل الخروج');
                localStorage.removeItem('user');
                localStorage.removeItem('userPermissions');
                setIsLoggedIn(false);
                setCurrentUser(null);
                setUserPermissions({});
            }
        } else if (response.status === 401) {
            // ✅ 401 = المستخدم اتحذف أو غير موجود - logout تلقائي
            console.warn('⚠️ 401 Unauthorized - المستخدم غير موجود - تسجيل الخروج');
            localStorage.removeItem('user');
            localStorage.removeItem('userPermissions');
            setIsLoggedIn(false);
            setCurrentUser(null);
            setUserPermissions({});
        } else {
            // فشل الاتصال بالشبكة (500, timeout) - نبقي المستخدم مسجل
            setIsLoggedIn(true);
            setCurrentUser(user);
            setUserPermissions(JSON.parse(user.Permissions || '{}'));
        }
    } catch (error) {
        console.error('خطأ في التحقق من المصادقة:', error);
        // في حالة فشل الاتصال، نبقي المستخدم مسجل الدخول
        const userData = localStorage.getItem('user');
        if (userData) {
            const user = JSON.parse(userData);
            setIsLoggedIn(true);
            setUserPermissions(JSON.parse(user.Permissions || '{}'));
        }
    } finally {
        setIsLoading(false);
    }
};

  const handleConfigSave = (config) => {
      setShowSetup(false);
      
      // إعادة تحميل الصفحة لتطبيق التكوين الجديد
      setTimeout(() => {
          window.location.reload();
      }, 500);
  };

  const handleLogin = async (user) => {
      await hydrateSharedSettings(); // الإعدادات المشتركة قبل ما الشاشات تفتح
      setIsLoggedIn(true);
      markScreenUnlocked();
      setIsLocked(false);
      try {
          setCurrentUser(user);
          setUserPermissions(JSON.parse(user?.Permissions || '{}'));
      } catch (error) {
          console.error('خطأ في تحليل الصلاحيات:', error);
      }
  };

  const handleLogout = () => {
      fetch(`${config.API_BASE_URL}/api/logout`, { method: 'POST' }).catch(() => {}); // إنهاء الجلسة على السيرفر
      setIsLoggedIn(false);
      setCurrentUser(null);
      setUserPermissions({});
      localStorage.removeItem('user');
      localStorage.removeItem('userPermissions');
      localStorage.removeItem(SCREEN_LOCK_KEYS.state);
      localStorage.removeItem(SCREEN_LOCK_KEYS.unlockedUntil);
      localStorage.removeItem(SCREEN_LOCK_KEYS.lastActivity);
      localStorage.removeItem(SCREEN_LOCK_KEYS.lastBrowserSeen);
      localStorage.removeItem(SCREEN_LOCK_KEYS.mode);
      sessionStorage.removeItem('screenLocked');
      sessionStorage.removeItem('screenUnlocked');
  };

  const hasPermission = (section, item) => {
      return userPermissions[section]?.[item] === true;
  };

  // ✅ مفتاح يتغير كل ما الصلاحيات تتغير - يجبر React على إعادة تقييم كل الـ Routes
  const permissionsKey = JSON.stringify(userPermissions);

  // عرض شاشة الإعداد في البداية
  if (showSetup) {
      return <Setup onSave={handleConfigSave} />;
  }

  // عرض شاشة التحميل أثناء التحقق من المصادقة
  if (isLoading) {
      return (
          <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">جاري التحميل...</p>
              </div>
          </div>
      );
  }

  // ✅ شاشة القفل - تظهر فوق كل حاجة لما الـ timer ينتهي
  if (isLocked && isLoggedIn) {
      return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-95" dir="rtl">
              <div className="bg-white rounded-2xl shadow-2xl p-8 w-96 text-center">
                  {/* أيقونة القفل */}
                  <div className="flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mx-auto mb-4">
                      <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                  </div>

                  <h2 className="text-xl font-bold text-gray-800 mb-1">الشاشة مقفولة</h2>
                  <p className="text-gray-500 text-sm mb-6">
                      مرحباً <strong>{currentUser?.FullName}</strong>، أدخل كلمة مرورك للمتابعة
                  </p>

                  {lockError && (
                      <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-2 rounded-lg mb-4 text-sm">
                          {lockError}
                      </div>
                  )}

                  <input
                      type="password"
                      className="w-full border-2 border-gray-200 rounded-lg p-3 text-center text-lg focus:outline-none focus:border-blue-500 mb-4"
                      placeholder="كلمة المرور"
                      value={lockPassword}
                      onChange={(e) => setLockPassword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                      autoFocus
                      disabled={lockLoading}
                  />

                  <button
                      onClick={handleUnlock}
                      disabled={lockLoading || !lockPassword.trim()}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white py-3 rounded-lg font-semibold transition-colors mb-3"
                  >
                      {lockLoading ? 'جاري التحقق...' : 'فتح القفل'}
                  </button>

                  {/* زر تسجيل الخروج كحل أخير */}
                  <button
                      onClick={() => { 
                          setIsLocked(false); 
                          handleLogout(); 
                      }}
                      className="w-full text-gray-400 hover:text-gray-600 text-sm py-2 transition-colors"
                  >
                      تسجيل الخروج بمستخدم آخر
                  </button>
              </div>
          </div>
      );
  }

  return (
      <Router>
          <PageTitleUpdater />
          <MasarExternalRouteHandler />
          <div dir="rtl" className="w-full max-w-full overflow-x-hidden">
              <Toaster 
                  position="top-right" 
                  toastOptions={{
                      duration: 4000,
                      style: {
                          direction: 'rtl',
                      },
                  }} 
              />

              <Routes key={permissionsKey}>
                  <Route
                      path="/login"
                      element={!isLoggedIn ? <Login onLogin={handleLogin} onShowSetup={() => setShowSetup(true)} /> : <Navigate to="/" />}
                  />

                  <Route
                      path="/"
                      element={isLoggedIn ? <RootRouteGate isLoggedIn={isLoggedIn}><Dashboard onLogout={handleLogout} /></RootRouteGate> : <Navigate to="/login" />}
                  />

                  <Route
                      path="/security"
                      element={
                          isLoggedIn && hasPermission("المستخدمين", "المستخدمين")
                              ? <SecurityCenter />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/users"
                      element={
                          isLoggedIn && hasPermission("المستخدمين", "المستخدمين")
                              ? <UserManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/companies"
                      element={
                          isLoggedIn && hasPermission("البيانات", "الشركات")
                              ? <CompanyManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/delegates"
                      element={
                          isLoggedIn && hasPermission("البيانات", "المناديب")
                              ? <DelegateManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/professions"
                      element={
                          isLoggedIn && hasPermission("البيانات", "المهن")
                              ? <ProfessionManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/tests"
                      element={
                          isLoggedIn && hasPermission("البيانات", "الاختبارات")
                              ? <TestManagement />
                              : <Navigate to="/" />
                      }
                  />


                  <Route
                      path="/work-lists"
                      element={
                          isLoggedIn && (
                              Object.prototype.hasOwnProperty.call(userPermissions?.['البيانات'] || {}, 'قوائم العمل')
                                  ? hasPermission("البيانات", "قوائم العمل")
                                  : (hasPermission("البيانات", "الاختبارات") || hasPermission("الجوازات", "الجوازات"))
                          )
                              ? <WorkLists />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/banks"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "الخزينة الشاملة")
                              ? <BanksManagement />
                              : <Navigate to="/" />
                      }
                  />

<Route
                      path="/settings/api-keys"
                      element={
                          isLoggedIn && (hasPermission("إعدادات", "مفاتيح الـ OCR") || hasPermission("المستخدمين", "المستخدمين"))
                              ? <APIKeysManager />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/human-resources"
                      element={
                          isLoggedIn && hasPermission("إدارة الموارد البشرية", "إدارة الموارد البشرية")
                              ? <HumanResourcesManagement />
                              : <Navigate to="/" />
                      }
                  />

<Route
                      path="/smart-repeat"
                      element={
                          isLoggedIn && (hasPermission("الجوازات", "التكرار الذكي") || hasPermission("المستخدمين", "المستخدمين"))
                              ? <SmartRepeat />
                              : <Navigate to="/" />
                      }
                  />
                  <Route
                      path="/passports"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الجوازات")
                              ? <PassportManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/passports/barcode-settings"
                      element={
                          isLoggedIn
                              ? <BarcodeSettingsPage />
                              : <Navigate to="/login" />
                      }
                  />

                  <Route
                      path="/passports-authentication"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الجوازات-أتصيل")
                              ? <PassportManagement type="authentication" />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/selection-preparation"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الاختيار والتجهيزات")
                              ? <SelectionAndPreparation />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/reports"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الكشوفات")
                              ? <Reports />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/flight-report"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الكشوفات")
                              ? <FlightReport />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/passport-report"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الكشوفات")
                              ? <PassportReport />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/fingerprint-report"
                      element={
                          isLoggedIn && hasPermission("الجوازات", "الكشوفات")
                              ? <FingerprintReport />
                              : <Navigate to="/" />
                      }
                  />










                  <Route
                      path="/db-sync"
                      element={
                          isLoggedIn && hasPermission("إدارة قاعدة البيانات", "مزامنة البيانات")
                              ? <DatabaseManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/data-management"
                      element={
                          isLoggedIn && hasPermission("إدارة قاعدة البيانات", "إدارة البيانات")
                              ? <DataManagement />
                              : <Navigate to="/" />
                      }
                  />

<Route
  path="/db-backup"
  element={
    isLoggedIn && (hasPermission("إدارة قاعدة البيانات", "النسخ الاحتياطي") || 
                   hasPermission("إدارة قاعدة البيانات", "النسخ الاحتياطي وإدارة قاعدة البيانات"))
      ? <DatabaseBackup />
      : <Navigate to="/" />
  }
/>

                  <Route
                      path="/db-delete"
                      element={
                          isLoggedIn && hasPermission("إدارة قاعدة البيانات", "حذف قاعدة البيانات")
                              ? <DatabaseDelete />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/visa-net-registration"
                      element={
                          isLoggedIn && hasPermission("التأشيرات", "تسجيل النت")
                              ? <VisaNetRegistration />
                              : <Navigate to="/" />
                      }
                  />


                  <Route
                      path="/visas"
                      element={
                          isLoggedIn && hasPermission("التأشيرات", "السفر")
                              ? <VisaManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/consulate"
                      element={
                          isLoggedIn && hasPermission("التأشيرات", "القنصلية")
                              ? <ConsulateManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/employee-accounts"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "حسابات الموظفين")
                              ? <EmployeeManagement />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/company-transactions"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "إيرادات ومصروفات الشركة")
                              ? <CompanyTransactions />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/client-accounts"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "حسابات العملاء")
                              ? <ClientAccounts />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/delegate-accounts"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "حسابات المناديب")
                              ? <DelegateAccounts />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/financial-reports"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "التقارير")
                              ? <FinancialReports />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
                      path="/categories"
                      element={
                          isLoggedIn && hasPermission("الحسابات", "التصنيفات")
                              ? <Categories />
                              : <Navigate to="/" />
                      }
                  />

                  <Route
  path="/periodic-follow-up"
  element={
    isLoggedIn && hasPermission("التأشيرات", "المتابعة الدورية")
      ? <PeriodicFollowUp />
      : <Navigate to="/" />
  }
/>

<Route
  path="/visa-reports"
  element={
    isLoggedIn && hasPermission("التأشيرات", "تقارير التأشيرات")
      ? <VisaReports />
      : <Navigate to="/" />
  }
/>

<Route
  path="/archive-management"
  element={
    isLoggedIn && hasPermission("إدارة قاعدة البيانات", "إدارة الأرشيف")
      ? <ArchiveManagement />
      : <Navigate to="/" />
  }
/>


                  <Route
                      path="*"
                      element={
                          isLoggedIn
                              ? <Navigate to="/" />
                              : <Navigate to="/login" />
                      }
                  />
              </Routes>
              <FloatingQuickNavigator isLoggedIn={isLoggedIn} userPermissions={userPermissions} />
              <MobileGlobalBottomNav isLoggedIn={isLoggedIn} userPermissions={userPermissions} />
              <InternalChat isLoggedIn={isLoggedIn} currentUser={currentUser} userPermissions={userPermissions} />
          </div>
      </Router>
  );
}

export default App;